/** Paginated world geography APIs (/authn/world/countries, .../cities). Used by Create Account modal. */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"

const DEFAULT_PAGE_SIZE = 25

const buildPagedUrl = (basePath, { query = "", page = 0, size = DEFAULT_PAGE_SIZE }) => {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("size", String(size))
  if (query?.trim()) {
    params.set("q", query.trim())
  }
  return `${basePath}?${params.toString()}`
}

export const formatWorldCountryLabel = (countryItem) => {
  return countryItem?.name?.trim() || countryItem?.country?.trim() || ""
}

/** Display label for world city API rows, e.g. "Pilani, Rajasthan". */
export const formatWorldCityLabel = (cityItem) => {
  const city = cityItem?.name?.trim() || cityItem?.city?.trim() || ""
  const state =
    cityItem?.worldState?.name?.trim() ||
    cityItem?.geographyState?.state?.trim() ||
    cityItem?.geographyState?.name?.trim() ||
    cityItem?.state?.trim() ||
    ""
  if (city && state) return `${city}, ${state}`
  return city || state || ""
}

export const fetchWorldCountries = createAsyncThunk(
  "worldGeography/fetchCountries",
  async ({ query = "", page = 0, size = DEFAULT_PAGE_SIZE, append = false } = {}, { rejectWithValue }) => {
    try {
      const url = buildPagedUrl("/authn/world/countries", { query, page, size })
      const response = await get(url, { skipAuth: true })
      return {
        append: Boolean(append),
        query: query?.trim() || "",
        page,
        content: response?.content || [],
        last: Boolean(response?.last),
        totalPages: response?.totalPages ?? 0,
        number: response?.number ?? page,
      }
    } catch (error) {
      console.error("World countries API error:", error)
      return rejectWithValue(error?.message || "Failed to fetch countries")
    }
  },
)

export const fetchWorldCities = createAsyncThunk(
  "worldGeography/fetchCities",
  async (
    { countryId, query = "", page = 0, size = DEFAULT_PAGE_SIZE, append = false } = {},
    { rejectWithValue },
  ) => {
    try {
      if (!countryId) {
        return {
          append: Boolean(append),
          query: query?.trim() || "",
          page,
          countryId: null,
          content: [],
          last: true,
          totalPages: 0,
          number: page,
        }
      }

      const url = buildPagedUrl(`/authn/world/countries/${countryId}/cities`, { query, page, size })
      const response = await get(url, { skipAuth: true })
      return {
        append: Boolean(append),
        query: query?.trim() || "",
        page,
        countryId,
        content: response?.content || [],
        last: Boolean(response?.last),
        totalPages: response?.totalPages ?? 0,
        number: response?.number ?? page,
      }
    } catch (error) {
      console.error("World cities API error:", error)
      return rejectWithValue(error?.message || "Failed to fetch cities")
    }
  },
)

const emptyListState = () => ({
  items: [],
  query: "",
  page: 0,
  totalPages: 0,
  last: true,
  loading: false,
  loadingMore: false,
  error: null,
})

const worldGeographySlice = createSlice({
  name: "worldGeography",
  initialState: {
    countries: emptyListState(),
    cities: {
      ...emptyListState(),
      countryId: null,
    },
  },
  reducers: {
    resetWorldCountries: (state) => {
      state.countries = emptyListState()
    },
    resetWorldCities: (state) => {
      state.cities = {
        ...emptyListState(),
        countryId: null,
      }
    },
    clearWorldGeography: (state) => {
      state.countries = emptyListState()
      state.cities = {
        ...emptyListState(),
        countryId: null,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorldCountries.pending, (state, action) => {
        const append = Boolean(action.meta.arg?.append)
        if (append) {
          state.countries.loadingMore = true
        } else {
          state.countries.loading = true
          const nextQuery = action.meta.arg?.query?.trim() ?? ""
          if (nextQuery !== (state.countries.query ?? "")) {
            state.countries.items = []
          }
        }
        state.countries.error = null
      })
      .addCase(fetchWorldCountries.fulfilled, (state, action) => {
        const { append, content, last, totalPages, number, query } = action.payload
        state.countries.loading = false
        state.countries.loadingMore = false
        state.countries.query = query
        state.countries.page = number
        state.countries.totalPages = totalPages
        state.countries.last = last
        state.countries.items = append ? [...state.countries.items, ...content] : content
      })
      .addCase(fetchWorldCountries.rejected, (state, action) => {
        state.countries.loading = false
        state.countries.loadingMore = false
        state.countries.error = action.payload
        if (!action.meta.arg?.append) {
          state.countries.items = []
        }
      })

      .addCase(fetchWorldCities.pending, (state, action) => {
        const append = Boolean(action.meta.arg?.append)
        if (append) {
          state.cities.loadingMore = true
        } else {
          state.cities.loading = true
          const nextQuery = action.meta.arg?.query?.trim() ?? ""
          const nextCountryId = action.meta.arg?.countryId ?? null
          const sameCountry =
            String(state.cities.countryId ?? "") === String(nextCountryId ?? "")
          if (!sameCountry || nextQuery !== (state.cities.query ?? "")) {
            state.cities.items = []
          }
        }
        state.cities.error = null
      })
      .addCase(fetchWorldCities.fulfilled, (state, action) => {
        const { append, content, last, totalPages, number, query, countryId } = action.payload
        state.cities.loading = false
        state.cities.loadingMore = false
        state.cities.query = query
        state.cities.page = number
        state.cities.totalPages = totalPages
        state.cities.last = last
        state.cities.countryId = countryId
        state.cities.items = append ? [...state.cities.items, ...content] : content
      })
      .addCase(fetchWorldCities.rejected, (state, action) => {
        state.cities.loading = false
        state.cities.loadingMore = false
        state.cities.error = action.payload
        if (!action.meta.arg?.append) {
          state.cities.items = []
        }
      })
  },
})

export const { resetWorldCountries, resetWorldCities, clearWorldGeography } = worldGeographySlice.actions

export default worldGeographySlice.reducer
